/**
 * d.nuvo Digital Skincare Consultant — theme.js v2.0
 * Features: Product carousel, review slider, video lazy-load,
 *           sticky ATC bar, quiz logic, cart AJAX, scroll reveal,
 *           mobile nav, email capture modal, concern navigation
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
   * 1. UTILITY HELPERS
   * ───────────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const moneyFmt = (cents, cur) => {
    const c = cur || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SGD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-SG', { style: 'currency', currency: c, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(cents / 100); }
    catch (e) { return c + ' ' + Math.round(cents / 100); }
  };
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  /* ─────────────────────────────────────────────
   * 2. SCROLL REVEAL ANIMATION
   * ───────────────────────────────────────────── */
  function initReveal() {
    const items = $$('.reveal, .reveal-left, .reveal-right, .reveal-up');
    if (!items.length) return;
    const showAll = () => items.forEach(el => el.classList.add('revealed'));
    if (!('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) { showAll(); return; }
    window.setTimeout(showAll, 2500); // safety net: never leave a section invisible
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('revealed');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    items.forEach(el => io.observe(el));
  }

  /* ─────────────────────────────────────────────
   * 3. STICKY HEADER
   * ───────────────────────────────────────────── */
  function initStickyHeader() {
    const header = $('.site-header');
    if (!header) return;
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 80) {
        header.classList.add('site-header--scrolled');
        header.classList.toggle('site-header--hidden', y > lastY && y > 300);
      } else {
        header.classList.remove('site-header--scrolled', 'site-header--hidden');
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ─────────────────────────────────────────────
   * 3b. CAMPAIGN HERO FILM
   * The supplied d.nuvo film is purely visual. It stays muted, pauses
   * for reduced-motion preferences, and never replaces the fallback image.
   * ───────────────────────────────────────────── */
  function initHeroFilm() {
    const film = $('[data-hero-film]');
    if (!film) return;
    const motionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    const pauseForReducedMotion = () => {
      if (motionQuery && motionQuery.matches) {
        film.pause();
        return true;
      }
      return false;
    };
    if (!pauseForReducedMotion()) film.play().catch(() => {});
    on(document, 'visibilitychange', () => {
      if (document.hidden) film.pause();
      else if (!pauseForReducedMotion()) film.play().catch(() => {});
    });
    if (motionQuery && motionQuery.addEventListener) {
      motionQuery.addEventListener('change', () => {
        if (!pauseForReducedMotion()) film.play().catch(() => {});
      });
    }
  }

  /* ─────────────────────────────────────────────
   * 4. MOBILE NAVIGATION
   * ───────────────────────────────────────────── */
  function initMobileNav() {
    const toggle = $('#mobileNavToggle');
    const nav = $('#mobileNav');
    const overlay = $('#mobileNavOverlay');
    if (!toggle || !nav) return;

    const open = () => {
      nav.classList.add('mobile-nav--open');
      overlay && overlay.classList.add('mobile-nav-overlay--visible');
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      nav.classList.remove('mobile-nav--open');
      overlay && overlay.classList.remove('mobile-nav-overlay--visible');
      document.body.style.overflow = '';
      toggle.setAttribute('aria-expanded', 'false');
    };

    on(toggle, 'click', () => nav.classList.contains('mobile-nav--open') ? close() : open());
    on(overlay, 'click', close);
    $$('[data-mobile-nav-close]').forEach(button => on(button, 'click', close));
    on(document, 'keydown', e => e.key === 'Escape' && close());

    // Concern dropdown in mobile nav
    $$('.mobile-nav-concern-toggle').forEach(btn => {
      on(btn, 'click', function () {
        const sub = this.nextElementSibling;
        if (sub) sub.classList.toggle('mobile-nav-sub--open');
        this.setAttribute('aria-expanded', sub && sub.classList.contains('mobile-nav-sub--open'));
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 5. PRODUCT IMAGE CAROUSEL
   * ───────────────────────────────────────────── */
  function initProductCarousels() {
    $$('.product-carousel').forEach(carousel => {
      const track = $('.product-carousel-track', carousel);
      const slides = $$('.product-carousel-slide', carousel);
      const thumbs = $$('.product-thumb-btn', carousel);
      const prev = $('.product-carousel-prev', carousel);
      const next = $('.product-carousel-next', carousel);
      const counter = $('.product-carousel-counter', carousel);
      if (!track || !slides.length) return;

      let current = 0;
      let startX = 0;
      let isDragging = false;

      const goTo = (idx) => {
        current = Math.max(0, Math.min(idx, slides.length - 1));
        track.style.transform = `translateX(-${current * 100}%)`;
        slides.forEach((s, i) => s.classList.toggle('product-carousel-slide--active', i === current));
        thumbs.forEach((t, i) => t.classList.toggle('product-thumb-btn--active', i === current));
        if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
      };

      on(prev, 'click', () => goTo(current - 1));
      on(next, 'click', () => goTo(current + 1));
      thumbs.forEach((t, i) => on(t, 'click', () => goTo(i)));

      // Touch/swipe support
      on(track, 'touchstart', e => { startX = e.touches[0].clientX; isDragging = true; }, { passive: true });
      on(track, 'touchend', e => {
        if (!isDragging) return;
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) goTo(diff > 0 ? current + 1 : current - 1);
        isDragging = false;
      });

      // Keyboard navigation
      on(carousel, 'keydown', e => {
        if (e.key === 'ArrowLeft') goTo(current - 1);
        if (e.key === 'ArrowRight') goTo(current + 1);
      });

      goTo(0);
    });
  }

  /* ─────────────────────────────────────────────
   * 6. REVIEW / TESTIMONIAL SLIDER
   * ───────────────────────────────────────────── */
  function initReviewSlider() {
    const slider = $('.reviews-slider');
    if (!slider) return;
    const track = $('.reviews-track', slider);
    const cards = $$('.review-card', slider);
    const dots = $$('.reviews-dot', slider);
    const prev = $('.reviews-prev', slider);
    const next = $('.reviews-next', slider);
    if (!track || !cards.length) return;

    let current = 0;
    let autoTimer;

    const getVisible = () => window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1;

    const goTo = (idx) => {
      const visible = getVisible();
      const max = Math.max(0, cards.length - visible);
      current = Math.max(0, Math.min(idx, max));
      const pct = (current / cards.length) * 100;
      track.style.transform = `translateX(-${pct}%)`;
      dots.forEach((d, i) => d.classList.toggle('reviews-dot--active', i === current));
    };

    const autoPlay = () => {
      autoTimer = setInterval(() => {
        const visible = getVisible();
        const max = Math.max(0, cards.length - visible);
        goTo(current >= max ? 0 : current + 1);
      }, 5000);
    };

    on(prev, 'click', () => { clearInterval(autoTimer); goTo(current - 1); autoPlay(); });
    on(next, 'click', () => { clearInterval(autoTimer); goTo(current + 1); autoPlay(); });
    dots.forEach((d, i) => on(d, 'click', () => { clearInterval(autoTimer); goTo(i); autoPlay(); }));

    // Touch swipe
    let startX = 0;
    on(track, 'touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    on(track, 'touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) { clearInterval(autoTimer); goTo(diff > 0 ? current + 1 : current - 1); autoPlay(); }
    });

    window.addEventListener('resize', () => goTo(current));
    goTo(0);
    autoPlay();
  }

  /* ─────────────────────────────────────────────
   * 7. VIDEO LAZY LOAD (TikTok / Instagram embeds)
   * ───────────────────────────────────────────── */
  function initVideoLazyLoad() {
    const placeholders = $$('.video-placeholder');
    if (!placeholders.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const ph = e.target;
          const src = ph.dataset.src;
          if (src) {
            const iframe = document.createElement('iframe');
            iframe.src = src;
            iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
            iframe.allowFullscreen = true;
            iframe.className = 'video-embed-iframe';
            iframe.title = ph.dataset.title || 'Video';
            ph.replaceWith(iframe);
          }
          io.unobserve(ph);
        }
      });
    }, { threshold: 0.1 });
    placeholders.forEach(ph => io.observe(ph));
  }

  /* ─────────────────────────────────────────────
   * 8. STICKY ADD-TO-CART BAR (Product Page)
   * ───────────────────────────────────────────── */
  function initStickyATC() {
    const stickyBar = $('#stickyAtc');
    const mainATC = $('#mainAddToCart');
    if (!stickyBar || !mainATC) return;

    const io = new IntersectionObserver((entries) => {
      stickyBar.classList.toggle('visible', !entries[0].isIntersecting);
    }, { threshold: 0 });
    io.observe(mainATC);

    // Sync variant selection to sticky bar
    const variantSelect = $('#productVariantSelect');
    const stickyVariantLabel = $('#stickyVariantLabel');
    if (variantSelect && stickyVariantLabel) {
      on(variantSelect, 'change', function () {
        stickyVariantLabel.textContent = this.options[this.selectedIndex].text;
      });
    }

    // Sticky ATC click — submit main form
    const stickyBtn = $('#stickyAtcBtn');
    on(stickyBtn, 'click', () => {
      const form = $('#productAddToCartForm');
      if (form) form.requestSubmit();
    });
  }

  /* ─────────────────────────────────────────────
   * 9. CART AJAX (Add to Cart + Drawer)
   * ───────────────────────────────────────────── */
  function initCartAjax() {
    const cartDrawer = $('#cart-drawer');
    const cartClose = $('#cart-close');
    const cartCount = $$('.cart-count');

    const announceCartSuccess = (form, btn) => {
      form.classList.add('is-added');
      btn && btn.classList.add('cart-add-button--success');
      if (cartDrawer) {
        cartDrawer.classList.add('cart-drawer--celebrate');
        window.setTimeout(() => cartDrawer.classList.remove('cart-drawer--celebrate'), 700);
      }
      const announcement = document.createElement('div');
      announcement.className = 'cart-add-success-announcement';
      announcement.setAttribute('role', 'status');
      announcement.textContent = 'Added to your bag';
      document.body.appendChild(announcement);
      window.setTimeout(() => announcement.remove(), 1800);
    };

    const openCart = () => {
      if (cartDrawer) cartDrawer.classList.add('open');
      document.body.style.overflow = 'hidden';
      refreshCart();
    };

    const closeCart = () => {
      if (cartDrawer) cartDrawer.classList.remove('open');
      document.body.style.overflow = '';
    };

    on(cartClose, 'click', closeCart);
    on(document, 'keydown', e => e.key === 'Escape' && closeCart());

    // Cart icon opens drawer
    $$('[data-cart-toggle]').forEach(btn => on(btn, 'click', e => { e.preventDefault(); openCart(); }));

    // Add to Cart form submission
    const addToCartForms = $$('.product-add-to-cart-form, [data-quick-add-form]');
    addToCartForms.forEach(form => {
      on(form, 'submit', async function (e) {
        e.preventDefault();
        const btn = $('[type="submit"]', this);
        const originalText = btn ? (btn.dataset.originalText || btn.textContent) : '';
        if (btn) { btn.textContent = 'Adding...'; btn.disabled = true; }

        try {
          const formData = new FormData(this);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          });
          if (!res.ok) throw new Error('Cart error');
          await res.json();
          if (btn) {
            btn.textContent = 'Added ✓';
          }
          announceCartSuccess(this, btn);
          openCart();
        } catch (err) {
          console.error('Add to cart failed:', err);
          if (btn) btn.textContent = 'Error — Try Again';
        } finally {
          if (btn) {
            window.setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
              form.classList.remove('is-added');
              btn.classList.remove('cart-add-button--success');
            }, 1200);
          }
        }
      });
    });

    async function refreshCart() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        updateCartCount(cart.item_count);
        renderCartItems(cart);
      } catch (err) {
        console.error('Cart refresh failed:', err);
      }
    }

    function updateCartCount(count) {
      cartCount.forEach(el => {
        el.textContent = count;
        el.classList.toggle('cart-count--hidden', count === 0);
      });
    }

    function renderCartItems(cart) {
      const itemsContainer = $('#cart-drawer-body');
      const totalEl = $('#cart-subtotal');
      if (!itemsContainer) return;

      if (!cart.items.length) {
        itemsContainer.innerHTML = '<p class="cart-drawer__empty">Your bag is empty.</p>';
        if (totalEl) totalEl.textContent = '';
        return;
      }

      itemsContainer.innerHTML = cart.items.map(item => `
          <div class="cart-item" data-key="${item.key}">
            <div class="cart-item__image"><img src="${item.image}" alt="${item.product_title}" loading="lazy"></div>
            <div class="cart-item__details">
              <p class="cart-item__name">${item.product_title}</p>
              <p class="cart-item__variant">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</p>
              <div class="cart-item__qty">
                <button class="qty-btn" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease quantity">−</button>
                <span>${item.quantity}</span>
                <button class="qty-btn" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div><p class="cart-item__price">${moneyFmt(item.final_line_price, cart.currency)}</p><button class="cart-item-remove" data-key="${item.key}" aria-label="Remove from bag">×</button></div>
          </div>
        `).join('');

      if (totalEl) totalEl.textContent = moneyFmt(cart.total_price, cart.currency);

      // Qty change / remove handlers
      $$('.qty-btn', itemsContainer).forEach(btn => {
        on(btn, 'click', () => updateCartItem(btn.dataset.key, parseInt(btn.dataset.qty)));
      });
      $$('.cart-item-remove', itemsContainer).forEach(btn => {
        on(btn, 'click', () => updateCartItem(btn.dataset.key, 0));
      });
    }

    async function updateCartItem(key, qty) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        refreshCart();
      } catch (err) {
        console.error('Cart update failed:', err);
      }
    }

    // Initial count on page load
    fetch('/cart.js').then(r => r.json()).then(c => updateCartCount(c.item_count)).catch(() => {});
  }

  /* ─────────────────────────────────────────────
   * 10. SKIN QUIZ (Multi-step quiz page)
   * ───────────────────────────────────────────── */
  function initSkinQuiz() {
    const quiz = $('#skinQuizWidget');
    if (!quiz) return;

    const steps = $$('.quiz-step', quiz);
    const progressBar = $('#quizProgressBar');
    const progressLabel = $('#quizProgressLabel');
    let answers = {};
    let currentStep = 0;

    const showStep = (idx) => {
      steps.forEach((s, i) => s.classList.toggle('quiz-step--active', i === idx));
      const pct = ((idx + 1) / steps.length) * 100;
      if (progressBar) progressBar.style.width = pct + '%';
      if (progressLabel) progressLabel.textContent = `Question ${idx + 1} of ${steps.length}`;
      currentStep = idx;
    };

    $$('.quiz-option-btn', quiz).forEach(btn => {
      on(btn, 'click', function () {
        const step = this.closest('.quiz-step');
        const key = step ? step.dataset.question : null;
        if (key) answers[key] = this.dataset.value;
        $$('.quiz-option-btn', step).forEach(b => b.classList.remove('quiz-option-btn--selected'));
        this.classList.add('quiz-option-btn--selected');
        setTimeout(() => {
          if (currentStep < steps.length - 1) {
            showStep(currentStep + 1);
          } else {
            showQuizResult();
          }
        }, 350);
      });
    });

    $$('.quiz-back-btn', quiz).forEach(btn => {
      on(btn, 'click', () => showStep(Math.max(0, currentStep - 1)));
    });

    function showQuizResult() {
      const resultSection = $('#quizResult');
      if (!resultSection) return;
      steps.forEach(s => s.classList.remove('quiz-step--active'));
      resultSection.classList.add('quiz-result--visible');
      resultSection.hidden = false;

      // Determine concern from answers
      const concern = answers.concern || answers.primary_concern || 'barrier';
      const concernLabels = {
        barrier: 'Damaged Barrier',
        sensitivity: 'Sensitive / Reactive Skin',
        acne: 'Acne & Breakouts',
        dullness: 'Dullness & Dark Spots',
        aging: 'Fine Lines & Aging'
      };

      const resultTitle = $('#quizResultTitle');
      const resultDesc = $('#quizResultDesc');
      const resultProducts = $('#quizResultProducts');

      if (resultTitle) resultTitle.textContent = `Your Skin Profile: ${concernLabels[concern] || 'Balanced Skin'}`;
      if (resultDesc) resultDesc.textContent = `Based on your answers, your primary concern is ${(concernLabels[concern] || '').toLowerCase()}. Here is your personalised d.nuvo routine.`;

      // Redirect to routine builder with pre-selected concern
      const routineLink = $('#quizRoutineLink');
      if (routineLink) routineLink.href = `/pages/routine-builder#concern-${concern}`;
    }

    showStep(0);
  }

  /* ─────────────────────────────────────────────
   * 11. CONCERN NAVIGATION (Homepage concern grid)
   * ───────────────────────────────────────────── */
  function initConcernNav() {
    $$('.concern-card').forEach(card => {
      on(card, 'mouseenter', function () {
        this.querySelector('.concern-card-overlay') && this.querySelector('.concern-card-overlay').classList.add('concern-card-overlay--visible');
      });
      on(card, 'mouseleave', function () {
        this.querySelector('.concern-card-overlay') && this.querySelector('.concern-card-overlay').classList.remove('concern-card-overlay--visible');
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 12. INGREDIENT ENCYCLOPEDIA TABS
   * ───────────────────────────────────────────── */
  function initIngredientTabs() {
    const enc = $('.ingredient-encyclopedia');
    if (!enc) return;
    const tabs = $$('.ingredient-tab', enc);
    const panels = $$('.ingredient-panel', enc);

    tabs.forEach(tab => {
      on(tab, 'click', function () {
        const target = this.dataset.ingredient;
        tabs.forEach(t => t.classList.remove('ingredient-tab--active'));
        panels.forEach(p => p.classList.remove('ingredient-panel--active'));
        this.classList.add('ingredient-tab--active');
        const panel = $(`[data-panel="${target}"]`, enc);
        if (panel) panel.classList.add('ingredient-panel--active');
      });
    });

    if (tabs.length) tabs[0].click();
  }

  /* ─────────────────────────────────────────────
   * 13. PRODUCT INGREDIENT TOOLTIPS
   * ───────────────────────────────────────────── */
  function initIngredientTooltips() {
    const tooltips = $$('[data-ingredient-tooltip]');
    if (!tooltips.length) return;

    const getParts = (tooltip) => ({
      trigger: $('[data-ingredient-tooltip-trigger]', tooltip),
      content: $('[data-ingredient-tooltip-content]', tooltip)
    });

    const close = (tooltip, returnFocus = false) => {
      const { trigger, content } = getParts(tooltip);
      tooltip.classList.remove('ingredient-tooltip--open');
      trigger && trigger.setAttribute('aria-expanded', 'false');
      content && content.setAttribute('aria-hidden', 'true');
      if (returnFocus && trigger) trigger.focus();
    };

    const open = (tooltip) => {
      tooltips.forEach(item => { if (item !== tooltip) close(item); });
      const { trigger, content } = getParts(tooltip);
      tooltip.classList.add('ingredient-tooltip--open');
      trigger && trigger.setAttribute('aria-expanded', 'true');
      content && content.setAttribute('aria-hidden', 'false');
    };

    tooltips.forEach(tooltip => {
      const { trigger } = getParts(tooltip);
      if (!trigger) return;

      on(trigger, 'mouseenter', () => open(tooltip));
      on(trigger, 'mouseleave', () => {
        if (document.activeElement !== trigger) close(tooltip);
      });
      on(trigger, 'focus', () => open(tooltip));
      on(trigger, 'blur', () => {
        window.setTimeout(() => {
          if (!tooltip.contains(document.activeElement)) close(tooltip);
        }, 0);
      });
      on(trigger, 'click', (event) => {
        event.preventDefault();
        tooltip.classList.contains('ingredient-tooltip--open') ? close(tooltip) : open(tooltip);
      });
      on(tooltip, 'mouseenter', () => open(tooltip));
      on(tooltip, 'mouseleave', () => {
        if (!tooltip.contains(document.activeElement)) close(tooltip);
      });
    });

    on(document, 'pointerdown', event => {
      tooltips.forEach(tooltip => {
        if (!tooltip.contains(event.target)) close(tooltip);
      });
    });
    on(document, 'keydown', event => {
      if (event.key !== 'Escape') return;
      const openTooltip = $('.ingredient-tooltip--open');
      if (openTooltip) close(openTooltip, true);
    });
  }

  /* ─────────────────────────────────────────────
   * 14. TRANSPARENT ROUTINE REWARD
   * ───────────────────────────────────────────── */
  function initRoutineReward() {
    const reward = $('[data-routine-reward]');
    if (!reward) return;
    const overlay = $('[data-routine-reward-overlay]', reward);
    const dialog = $('[data-routine-reward-dialog]', reward);
    const form = $('[data-routine-reward-form]', reward);
    const success = $('[data-routine-reward-success]', reward);
    const error = $('[data-routine-reward-error]', reward);
    const email = $('#routine-reward-email', reward);
    const openers = $$('[data-routine-reward-open]', reward);
    const closers = $$('[data-routine-reward-close]', reward);

    if (!overlay || !dialog || !form) return;
    let lastFocused = null;
    const open = () => {
      lastFocused = document.activeElement;
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      window.setTimeout(() => email ? email.focus() : dialog.focus(), 0);
    };
    const close = () => {
      overlay.hidden = true;
      document.body.style.overflow = '';
      lastFocused && lastFocused.focus && lastFocused.focus();
    };

    openers.forEach(button => on(button, 'click', open));
    closers.forEach(button => on(button, 'click', close));
    on(overlay, 'click', event => { if (event.target === overlay) close(); });
    on(document, 'keydown', event => { if (event.key === 'Escape' && !overlay.hidden) close(); });
    const markDismissed = () => { try { window.localStorage.setItem('dnuvo:routine-reward:dismissed', String(Date.now())); } catch (e) {} };
    on(overlay, 'click', event => { if (event.target === overlay) markDismissed(); });
    closers.forEach(button => on(button, 'click', markDismissed));

    if (reward.hasAttribute('data-routine-reward-auto')) {
      const delay = (parseInt(reward.getAttribute('data-routine-reward-delay'), 10) || 6) * 1000;
      const frequencyDays = parseInt(reward.getAttribute('data-routine-reward-frequency'), 10) || 7;
      let lastSeen = 0;
      try { lastSeen = parseInt(window.localStorage.getItem('dnuvo:routine-reward:dismissed'), 10) || 0; } catch (e) {}
      const dueAgain = !lastSeen || (Date.now() - lastSeen) > frequencyDays * 24 * 60 * 60 * 1000;
      if (dueAgain) window.setTimeout(open, delay);
    }

    on(form, 'submit', async event => {
      event.preventDefault();
      if (!form.checkValidity()) { form.reportValidity(); return; }
      if (error) error.hidden = true;
      const button = $('[type="submit"]', form);
      const originalLabel = button && button.textContent;
      if (button) { button.disabled = true; button.textContent = 'Saving your choice…'; }
      try {
        const response = await fetch(form.action, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Routine Reward form submission failed');
        form.hidden = true;
        if (success) success.hidden = false;
        markDismissed();
        window.dispatchEvent(new CustomEvent('dnuvo:routine-reward-consented'));
      } catch (err) {
        console.error('Routine Reward submission failed:', err);
        if (error) error.hidden = false;
      } finally {
        if (button) { button.disabled = false; button.textContent = originalLabel; }
      }
    });

    const claim = $('[data-routine-reward-claim]', reward);
    on(claim, 'click', () => window.dispatchEvent(new CustomEvent('dnuvo:routine-reward-claim')));
  }

  /* ─────────────────────────────────────────────
   * 15. INGREDIENT DECISION GUIDE
   * ───────────────────────────────────────────── */
  function initIngredientDecisionGuide() {
    $$('[data-decision-tabs]').forEach(tabList => {
      const tabs = $$('[data-decision-tab]', tabList);
      const root = tabList.closest('.ingredient-decision-guide');
      if (!root || !tabs.length) return;
      const activate = tab => {
        const key = tab.dataset.decisionTab;
        tabs.forEach(item => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        $$('[data-decision-panel]', root).forEach(panel => {
          panel.hidden = panel.dataset.decisionPanel !== key;
        });
      };
      tabs.forEach((tab, index) => {
        on(tab, 'click', () => activate(tab));
        on(tab, 'keydown', event => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          tabs[nextIndex].focus();
          activate(tabs[nextIndex]);
        });
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 17. ROUTINE PRODUCT LISTS
   * ───────────────────────────────────────────── */
  function initRoutineProductLists() {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    $$('[data-routine-product-list]').forEach(list => {
      const status = $('[data-routine-product-status]', list);
      const ready = () => {
        list.classList.remove('routine-product-list--loading');
        list.classList.add('routine-product-list--ready');
        list.setAttribute('aria-busy', 'false');
        if (status) status.textContent = 'Individual product links ready.';
      };

      if (reduceMotion) {
        ready();
      } else {
        list.classList.add('routine-product-list--loading');
        list.setAttribute('aria-busy', 'true');
        window.requestAnimationFrame(() => window.requestAnimationFrame(ready));
      }

      $$('[data-routine-product-link]', list).forEach(link => {
        on(link, 'click', event => {
          if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.classList.contains('is-loading')) return;
          link.classList.add('is-loading');
          link.setAttribute('aria-busy', 'true');
          if (status) status.textContent = `Opening ${link.textContent.trim()}.`;
        });
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 18. BRAND PROOF HUB RAILS
   * ───────────────────────────────────────────── */
  function initProofHubs() {
    $$('[data-proof-track]').forEach(track => {
      const root = track.closest('.dnuvo-proof-hub__rail-wrap');
      const previous = $('[data-proof-prev]', root);
      const next = $('[data-proof-next]', root);
      if (!root || !previous || !next) return;

      const updateControls = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll;
      };
      const move = direction => track.scrollBy({ left: direction * Math.max(track.clientWidth * .78, 260), behavior: 'smooth' });

      on(previous, 'click', () => move(-1));
      on(next, 'click', () => move(1));
      on(track, 'scroll', updateControls, { passive: true });
      on(window, 'resize', updateControls, { passive: true });
      updateControls();
    });
  }

  /* ─────────────────────────────────────────────
   * 16. EMAIL CAPTURE MODAL
   * ───────────────────────────────────────────── */
  function initEmailModal() {
    const modal = $('#emailCaptureModal');
    if (!modal) return;
    const close = $('#emailModalClose');
    const form = $('#emailCaptureForm');
    const STORAGE_KEY = 'dnuvo_email_modal_shown';

    const showModal = () => {
      modal.classList.add('email-modal--visible');
      document.body.style.overflow = 'hidden';
    };
    const hideModal = () => {
      modal.classList.remove('email-modal--visible');
      document.body.style.overflow = '';
      sessionStorage.setItem(STORAGE_KEY, '1');
    };

    on(close, 'click', hideModal);
    on(modal, 'click', e => e.target === modal && hideModal());
    on(document, 'keydown', e => e.key === 'Escape' && hideModal());

    on(form, 'submit', async function (e) {
      e.preventDefault();
      const email = $('[type="email"]', this).value;
      const btn = $('[type="submit"]', this);
      if (btn) btn.textContent = 'Subscribing...';
      // Klaviyo / newsletter form submission
      try {
        await fetch('/contact#contact_form', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `form_type=customer&email=${encodeURIComponent(email)}&tags=newsletter`
        });
      } catch (_) {}
      const successEl = $('#emailModalSuccess');
      if (successEl) { successEl.hidden = false; form.hidden = true; }
      setTimeout(hideModal, 2500);
    });

    if (!sessionStorage.getItem(STORAGE_KEY)) {
      setTimeout(showModal, 8000);
    }
  }

  /* ─────────────────────────────────────────────
   * 14. FREE SHIPPING PROGRESS BAR
   * ───────────────────────────────────────────── */
  function initFreeShippingBar() {
    const bar = $('.free-shipping-bar');
    if (!bar) return;
    const threshold = parseInt(bar.dataset.threshold || '80', 10) * 100; // in cents

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const total = cart.total_price;
        const remaining = Math.max(0, threshold - total);
        const pct = Math.min(100, (total / threshold) * 100);
        const progress = $('.free-shipping-progress', bar);
        const label = $('.free-shipping-label', bar);
        if (progress) progress.style.width = pct + '%';
        if (label) {
          label.textContent = remaining > 0
            ? `Add ${moneyFmt(remaining, cart.currency)} more for free shipping`
            : 'You qualify for free shipping!';
        }
      })
      .catch(() => {});
  }

  /* ─────────────────────────────────────────────
   * 15. PRODUCT PAGE — VARIANT SELECTION
   * ───────────────────────────────────────────── */
  function initVariantSelector() {
    const form = $('#productAddToCartForm');
    if (!form) return;
    const select = $('#productVariantSelect', form);
    const priceEl = $('#productPrice');
    const comparePriceEl = $('#productComparePrice');
    const atcBtn = $('#mainAddToCart');

    if (!select) return;

    on(select, 'change', function () {
      const opt = this.options[this.selectedIndex];
      const price = opt.dataset.price;
      const comparePrice = opt.dataset.comparePrice;
      const available = opt.dataset.available !== 'false';

      if (priceEl && price) priceEl.textContent = moneyFmt(parseInt(price));
      if (comparePriceEl) {
        comparePriceEl.textContent = comparePrice && parseInt(comparePrice) > parseInt(price)
          ? moneyFmt(parseInt(comparePrice)) : '';
      }
      if (atcBtn) {
        atcBtn.disabled = !available;
        atcBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 16. BEFORE/AFTER COMPARISON SLIDER
   * ───────────────────────────────────────────── */
  function initBeforeAfter() {
    $$('.before-after-slider').forEach(slider => {
      const handle = $('.ba-handle', slider);
      const afterImg = $('.ba-after', slider);
      if (!handle || !afterImg) return;

      let dragging = false;

      const setPosition = (x) => {
        const rect = slider.getBoundingClientRect();
        const pct = Math.max(0, Math.min(100, ((x - rect.left) / rect.width) * 100));
        handle.style.left = pct + '%';
        afterImg.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
      };

      on(handle, 'mousedown', () => { dragging = true; });
      on(document, 'mouseup', () => { dragging = false; });
      on(document, 'mousemove', e => { if (dragging) setPosition(e.clientX); });

      on(handle, 'touchstart', () => { dragging = true; }, { passive: true });
      on(document, 'touchend', () => { dragging = false; });
      on(document, 'touchmove', e => { if (dragging) setPosition(e.touches[0].clientX); }, { passive: true });

      setPosition(slider.getBoundingClientRect().left + slider.offsetWidth * 0.5);
    });
  }

  /* ─────────────────────────────────────────────
   * 17. RESULTS COUNTER ANIMATION
   * ───────────────────────────────────────────── */
  function initCounters() {
    const counters = $$('.stat-number[data-target]');
    if (!counters.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el = e.target;
        const target = parseFloat(el.dataset.target);
        const prefix = el.dataset.prefix || '';
        const suffix = el.dataset.suffix || '';
        const duration = 1800;
        const start = performance.now();
        const animate = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3);
          const value = target * ease;
          el.textContent = prefix + (Number.isInteger(target) ? Math.round(value) : value.toFixed(1)) + suffix;
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    counters.forEach(c => io.observe(c));
  }

  /* ─────────────────────────────────────────────
   * 18. CONCERN PAGE — TAB NAVIGATION
   * ───────────────────────────────────────────── */
  function initConcernPageTabs() {
    const tabNav = $('.concern-tab-nav');
    if (!tabNav) return;
    const tabs = $$('.concern-tab', tabNav);
    const panels = $$('.concern-tab-panel');

    tabs.forEach(tab => {
      on(tab, 'click', function () {
        const target = this.dataset.tab;
        tabs.forEach(t => t.classList.remove('concern-tab--active'));
        panels.forEach(p => p.classList.remove('concern-tab-panel--active'));
        this.classList.add('concern-tab--active');
        const panel = document.getElementById('tab-' + target);
        if (panel) panel.classList.add('concern-tab-panel--active');
      });
    });

    if (tabs.length) tabs[0].click();
  }

  /* ─────────────────────────────────────────────
   * 18b. COLLECTION FILTER / SORT / PAGINATION — AJAX
   * Swaps the results in place so applying a filter never resets scroll
   * position or jumps the page anywhere (no full navigation happens).
   * ───────────────────────────────────────────── */
  function initCollectionAjaxFilter() {
    let app = $('#collection-app');
    if (!app) return;

    const bindApp = () => {
      app = $('#collection-app');
      if (!app) return;

      $$('.collection-sidebar__heading a, .pagination a', app).forEach(a => {
        on(a, 'click', function (e) {
          if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          go(this.href);
        });
      });

      $$('.collection-filter-form, .collection-sort-form', app).forEach(form => {
        on(form, 'submit', function (e) {
          e.preventDefault();
          const params = new URLSearchParams(new FormData(this));
          const action = this.getAttribute('action') || window.location.pathname;
          const qs = params.toString();
          go(qs ? `${action}?${qs}` : action);
        });
      });
    };

    const swap = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('#collection-app');
      if (!next || !app) return;
      app.innerHTML = next.innerHTML;
      bindApp();
    };

    function go(url, push = true) {
      if (app) app.setAttribute('aria-busy', 'true');
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(r => r.text())
        .then(html => {
          swap(html);
          if (push) window.history.pushState({ collectionFilter: true }, '', url);
          if (app) app.removeAttribute('aria-busy');
        })
        .catch(() => { window.location.href = url; });
    }

    bindApp();
    window.addEventListener('popstate', () => go(window.location.href, false));
  }

  /* ─────────────────────────────────────────────
   * 18c. SHOP PAGE — SKIN CONDITION + LIFESTYLE FILTERS
   * Client-side, additive-within-a-group, best-match-first across groups.
   * Selecting a condition AND a lifestyle never hides everything: products
   * matching both surface first as "best match," products matching only one
   * surface below as "you might also like" — nothing is ever filtered down
   * to zero just because two tags don't happen to overlap on the same SKU.
   * ───────────────────────────────────────────── */
  function initShopFilters() {
    const app = $('[data-filter-app]');
    if (!app) return;

    const state = { condition: new Set(), lifestyle: new Set() };
    const parseTags = (str) => (str || '').split('||').map(s => s.trim()).filter(Boolean);

    const apply = () => {
      const grid = $('[data-product-grid]', app);
      if (!grid) return;
      const items = $$('[data-product-item]', grid);
      const alsoLabel = $('[data-also-matches-label]', app);
      const clearBtn = $('[data-filter-clear]', app);
      const statusEl = $('[data-filter-status]', app);
      const noMatches = $('[data-no-matches]', app);
      const countEl = $('[data-results-count]', app);
      const activeGroupCount = (state.condition.size ? 1 : 0) + (state.lifestyle.size ? 1 : 0);

      if (!activeGroupCount) {
        items.forEach(item => { item.hidden = false; item.classList.remove('is-partial-match'); });
        if (clearBtn) clearBtn.hidden = true;
        if (statusEl) statusEl.hidden = true;
        if (alsoLabel) alsoLabel.hidden = true;
        if (noMatches) noMatches.hidden = true;
        if (countEl) { countEl.hidden = false; countEl.textContent = items.length + (items.length === 1 ? ' product' : ' products'); }
        return;
      }

      if (clearBtn) clearBtn.hidden = false;

      const fullItems = [];
      const partialItems = [];
      items.forEach(item => {
        const cTags = parseTags(item.dataset.conditionTags);
        const lTags = parseTags(item.dataset.lifestyleTags);
        let matchedGroups = 0;
        if (state.condition.size && cTags.some(t => state.condition.has(t))) matchedGroups++;
        if (state.lifestyle.size && lTags.some(t => state.lifestyle.has(t))) matchedGroups++;

        if (matchedGroups === activeGroupCount) {
          item.hidden = false;
          item.classList.remove('is-partial-match');
          fullItems.push(item);
        } else if (matchedGroups > 0) {
          item.hidden = false;
          item.classList.add('is-partial-match');
          partialItems.push(item);
        } else {
          item.hidden = true;
          item.classList.remove('is-partial-match');
        }
      });

      // Never let the grid go empty just because two tags don't overlap on one SKU:
      // if nothing matches every active group, treat the partial matches as the main set.
      let shown = fullItems;
      let alsoShown = partialItems;
      if (!fullItems.length && partialItems.length) {
        partialItems.forEach(item => item.classList.remove('is-partial-match'));
        shown = partialItems;
        alsoShown = [];
      }

      shown.forEach(item => grid.appendChild(item));
      if (alsoLabel) {
        if (alsoShown.length) {
          grid.insertBefore(alsoLabel, alsoShown[0]);
          alsoLabel.hidden = false;
        } else {
          alsoLabel.hidden = true;
        }
      }
      alsoShown.forEach(item => grid.appendChild(item));

      const total = shown.length + alsoShown.length;
      if (noMatches) noMatches.hidden = total > 0;
      if (countEl) { countEl.hidden = false; countEl.textContent = total + (total === 1 ? ' product' : ' products'); }
      if (statusEl) {
        statusEl.hidden = false;
        statusEl.textContent = alsoShown.length
          ? `Showing ${shown.length} best match${shown.length === 1 ? '' : 'es'} and ${alsoShown.length} you might also like.`
          : `Showing ${shown.length} best match${shown.length === 1 ? '' : 'es'} for your selection.`;
      }
    };

    on(app, 'click', e => {
      const pill = e.target.closest('[data-filter-tag]');
      if (pill) {
        const set = state[pill.dataset.filterGroup];
        if (!set) return;
        if (set.has(pill.dataset.filterTag)) {
          set.delete(pill.dataset.filterTag);
          pill.setAttribute('aria-pressed', 'false');
          pill.classList.remove('active');
        } else {
          set.add(pill.dataset.filterTag);
          pill.setAttribute('aria-pressed', 'true');
          pill.classList.add('active');
        }
        apply();
        return;
      }
      if (e.target.closest('[data-filter-clear]')) {
        state.condition.clear();
        state.lifestyle.clear();
        $$('[data-filter-tag]', app).forEach(b => { b.setAttribute('aria-pressed', 'false'); b.classList.remove('active'); });
        apply();
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 19. SMOOTH SCROLL FOR ANCHOR LINKS
   * ───────────────────────────────────────────── */
  function initSmoothScroll() {
    $$('a[href^="#"]').forEach(a => {
      on(a, 'click', function (e) {
        const target = document.getElementById(this.getAttribute('href').slice(1));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
   * 20. INIT ALL
   * ───────────────────────────────────────────── */
  function init() {
    initReveal();
    initStickyHeader();
    initHeroFilm();
    initMobileNav();
    initProductCarousels();
    initReviewSlider();
    initVideoLazyLoad();
    initStickyATC();
    initCartAjax();
    initSkinQuiz();
    initConcernNav();
    initIngredientTabs();
    initIngredientTooltips();
    initRoutineReward();
    initIngredientDecisionGuide();
    initRoutineProductLists();
    initProofHubs();
    initCollectionAjaxFilter();
    initShopFilters();
    initEmailModal();
    initFreeShippingBar();
    initVariantSelector();
    initBeforeAfter();
    initCounters();
    initConcernPageTabs();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
